/**
 * Shared language primitives for the app. Single source of truth for the set of
 * languages the UI (and the reusable <LanguageSelect>) offers. Only fr/en are
 * enabled today; adding a locale here + a translation file is the whole change.
 *
 * This is the UI-language axis (per-user). It is DISTINCT from a brand's
 * `workingLanguage` (AI-output prose) and `targetLanguages` (measurement).
 */
export type UiLanguage = 'fr' | 'en';

export const SUPPORTED_UI_LANGUAGES: UiLanguage[] = ['fr', 'en'];

export const DEFAULT_UI_LANGUAGE: UiLanguage = 'fr';

/** Flag emoji + native label for each language, for the selector UI. */
export const LANGUAGE_META: Record<UiLanguage, { flag: string; label: string }> = {
  fr: { flag: '🇫🇷', label: 'Français' },
  en: { flag: '🇬🇧', label: 'English' },
};

/** Coerce any string (e.g. navigator.language, a stored value) to a supported code. */
export function normalizeUiLanguage(value: string | null | undefined): UiLanguage {
  const code = (value || '').slice(0, 2).toLowerCase();
  return (SUPPORTED_UI_LANGUAGES as string[]).includes(code)
    ? (code as UiLanguage)
    : DEFAULT_UI_LANGUAGE;
}

/**
 * Carries the language the user was browsing the marketing site in (fr/en)
 * across the Cognito hosted-UI redirect during signup, so a brand-new tenant
 * doesn't land back on a language that disagrees with what they picked on
 * traaker.ai. Written by the /signup page from a `?lang=` query param handed
 * off by the marketing site; consumed + cleared once by AuthContext on first
 * login (before any persisted `user.settings.uiLanguage` exists).
 */
export const SIGNUP_LANG_STASH_KEY = 'traaker_signup_lang';

function readStashedSignupLanguage(): string | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(SIGNUP_LANG_STASH_KEY) : null;
  } catch {
    return null;
  }
}

/**
 * Resolve the initial UI language before the user profile loads: stored user
 * preference wins, else the marketing-site signup language (if the user just
 * came through the signup funnel), else the browser language, else the
 * default. The caller passes the persisted `user.settings.uiLanguage` once
 * it's available.
 */
export function resolveInitialUiLanguage(stored?: string | null): UiLanguage {
  if (stored) return normalizeUiLanguage(stored);
  const stashed = readStashedSignupLanguage();
  if (stashed) return normalizeUiLanguage(stashed);
  if (typeof navigator !== 'undefined' && navigator.language) {
    return normalizeUiLanguage(navigator.language);
  }
  return DEFAULT_UI_LANGUAGE;
}
